import { useState, Dispatch, SetStateAction, useEffect, useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { Input, Checkbox, Space, Spin } from 'antd'
import { Rule } from 'antd/lib/form'

import styles from './DrawdownForm.module.scss'
import useStore from 'base/store'
import { DocumentsUpload, TDocumentsCollection } from 'modules/documents'
import {
  Box,
  Text,
  Button,
  Form,
  FormItem,
  InputAmount,
  FilesIcon,
  useForm,
  useWatch,
} from 'common/components'
import { useInputFocus } from 'common/hooks'
import { FIELD_RULES } from 'common/constants'
import { FileInterface } from 'common/types'
import { DrawdownDraftItem, useCreateDrawdown, useCreateDrawdownDraft, useGetDrawdownDraft } from '..'
import { OperateTypeEnum } from 'modules/documents/components/DocumentsUpload'

interface DrawdownFormProps {
  setDrawdownUuid?: Dispatch<SetStateAction<string>>
  onClose: () => void
}

export default function DrawdownForm({
  setDrawdownUuid,
  onClose,
}: DrawdownFormProps) {
  const [initialValues] = useState<any>({certificates: []});
  const [documentsError, setDocumentsError] = useState(false);
  // 只有在点击submit没有校验通过后，才会在每次上传、下载文件时做实时校验的提醒
  const [isClikedSubmit, setClikedSubmit] = useState(false);
  const params = useParams()
  const role = useStore((state) => state.role)
  let { data: drawdownDraft, mutate: getDrawdownDraft, isMutating: isGetDraftMutating } = useGetDrawdownDraft(params.uuid);
  const { mutate: createDrawdown, isMutating } = useCreateDrawdown(
    params.uuid,
    (drawdownUuid) => {
      // 草稿保存后清空数据
      drawdownDraft = {} as DrawdownDraftItem;
      setDrawdownUuid && setDrawdownUuid(drawdownUuid)
      onClose()
    }
  )
  const { mutate: saveDrawdownm, isMutating: isSaveMutating } = useCreateDrawdownDraft(
    () => onClose()
  );

  const [form] = useForm()
  const [drawdownNotices, setDrawdownNotices] = useState<FileInterface[]>([])
  const [invoices, setInvoices] = useState<FileInterface[]>([])
  const [developmentSitePhotos, setDevelopmentSitePhotos] = useState<
    FileInterface[]
  >([])
  const [buildingInspections, setBuildingInspections] = useState<
    FileInterface[]
  >([])
  const [geotechAndSurveyReports, setGeotechAndSurveyReports] = useState<
    FileInterface[]
  >([])
  const [engineerReports, setEngineerReports] = useState<FileInterface[]>([])
  const [
    utilityCertificationAndCompliance,
    setUtilityCertificationAndCompliance,
  ] = useState<FileInterface[]>([])
  const [qsReports, setQsReports] = useState<FileInterface[]>([])
  const [cccAndTitles, setCccAndTitles] = useState<FileInterface[]>([])
  const [preSales, setPreSales] = useState<FileInterface[]>([])
  const certificatesValue = useWatch('certificates', form)
  const inputRef = useInputFocus()

  useEffect(() => {
    (role.manager || role.coordinator || role.user) && getDrawdownDraft();
  }, [getDrawdownDraft, role])

  const certificatesGroup = {
    buildingInspections: {
      documents: buildingInspections,
      setDocuments: setBuildingInspections,
    },
    geotechAndSurveyReports: {
      documents: geotechAndSurveyReports,
      setDocuments: setGeotechAndSurveyReports,
    },
    engineerReports: {
      documents: engineerReports,
      setDocuments: setEngineerReports,
    },
    utilityCertificationAndCompliance: {
      documents: utilityCertificationAndCompliance,
      setDocuments: setUtilityCertificationAndCompliance,
    },
    qsReports: {
      documents: qsReports,
      setDocuments: setQsReports,
    },
    cccAndTitles: {
      documents: cccAndTitles,
      setDocuments: setCccAndTitles,
    },
    preSales: {
      documents: preSales,
      setDocuments: setPreSales,
    },
  }
  const certificatesGroupEntries = Object.entries(certificatesGroup)

  const handleFinish = ({ documents, certificates, ...values }: any) => {
    const newValues = {
      ...values,
      drawdownNotices: getDocumentsUuids(drawdownNotices),
      invoices: getDocumentsUuids(invoices),
      developmentSitePhotos: getDocumentsUuids(developmentSitePhotos),
    }
    if (!role.user) {
      newValues.approvedAmount = values.requestedAmount
    }
    certificatesGroupEntries.forEach(([groupKey, groupData]) => {
      newValues[groupKey] = certificatesValue.includes(groupKey)
        ? getDocumentsUuids(groupData.documents)
        : []
    })
    createDrawdown(newValues)
  }

  // 对草稿中的数据进行解析
  useEffect(() => {
    if (drawdownDraft && Object.keys(drawdownDraft)?.length) {
      const { title, requestedAmount, notes, drawdownNotices, invoices, certificates,
        developmentSitePhotos, buildingInspections, geotechAndSurveyReports, engineerReports, utilityCertificationAndCompliance, qsReports, cccAndTitles, preSales
      } = drawdownDraft;
      form.setFieldsValue({
        title,
        requestedAmount,
        notes,
        certificates,
      });
      drawdownNotices && setDrawdownNotices(drawdownNotices);
      invoices && setInvoices(invoices);
      developmentSitePhotos && setDevelopmentSitePhotos(developmentSitePhotos);
      buildingInspections && setBuildingInspections(buildingInspections);
      geotechAndSurveyReports && setGeotechAndSurveyReports(geotechAndSurveyReports);
      engineerReports && setEngineerReports(engineerReports);
      utilityCertificationAndCompliance && setUtilityCertificationAndCompliance(utilityCertificationAndCompliance);
      qsReports && setQsReports(qsReports);
      cccAndTitles && setCccAndTitles(cccAndTitles);
      preSales && setPreSales(preSales);
    }
  }, [drawdownDraft, form])


  // 保存为草稿，暂不提交
  const handleSave = () => {
    const newValues = {
      ...form.getFieldsValue(),
      drawdownNotices: formatDocumentsInfo(drawdownNotices),
      invoices: formatDocumentsInfo(invoices),
      developmentSitePhotos: formatDocumentsInfo(developmentSitePhotos),
    }

    certificatesGroupEntries.forEach(([groupKey, groupData]) => {
      newValues[groupKey] = certificatesValue.includes(groupKey)
        ? formatDocumentsInfo(groupData.documents)
        : []
    });
    // 判断表单是否有填写过内容 true-填写 false-没填写
    const isTouchedFiled = Object.values(newValues).some((item) => {
      // 确保item是(string | Array<string>)类型
      if (typeof item === 'string' || Array.isArray(item)) {
        return item.length > 0;
      }
      return false;
    });

    // 判断当前是否是在草稿的基础上save的
    const isBaseDraft = drawdownDraft && Object.entries(drawdownDraft).some(([key, value]) => {
      // 确保item是(string | Array<string>)类型
      if ((typeof value === 'string' || Array.isArray(value)) && key !== 'project') {
        return value.length;
      }
      return false;
    });
    // 非草稿打开的save，如果没有内容才会直接关闭弹窗。为了避免无意义的请求以及记录
    if (!isBaseDraft && !isTouchedFiled) {
      onClose();
      return;
    }
    saveDrawdownm({ ...newValues, project: params.uuid });
  }

  const documentsRules: Rule[] = [
    () => ({
      validator() {
        if(drawdownNotices.length ||
          invoices.length ||
          developmentSitePhotos.length ||
          certificatesGroupEntries.some(
            ([groupKey, groupData]) =>
              certificatesValue?.includes(groupKey) &&
              groupData.documents.length
          )) {
          setDocumentsError(false);
          return Promise.resolve();
        }
        setDocumentsError(true);
        return Promise.reject()
      },
    }),
  ]

  const getFilesNum = useMemo(() => {
    return (+drawdownNotices.length) +
      (+invoices.length) +
      (+developmentSitePhotos.length) +
      (+buildingInspections.length) +
      (+geotechAndSurveyReports.length) +
      (+engineerReports.length) +
      (+utilityCertificationAndCompliance.length) +
      (+qsReports.length) +
      (+cccAndTitles.length) +
      (+preSales.length)
  }, [buildingInspections.length, cccAndTitles.length, developmentSitePhotos.length,
  drawdownNotices.length, engineerReports.length, geotechAndSurveyReports.length,
  invoices.length, preSales.length, qsReports.length, utilityCertificationAndCompliance.length]);

  const handleUpload = useCallback((operateType: OperateTypeEnum) => {
    // save时不做校验，避免有歧义
    if(!isClikedSubmit) return;
    // 新增一个文件
    let curFilesCount = getFilesNum;
    if(!curFilesCount && operateType === OperateTypeEnum.ADD) {
      setDocumentsError(false);
      return;
    }
    // 减少一个文件
    if(curFilesCount === 1 && operateType === OperateTypeEnum.REMOVE) {
      setDocumentsError(true);
      return;
    }
  }, [getFilesNum, isClikedSubmit]);

  return (
    <Spin spinning={isGetDraftMutating}>
      <Form
        className={styles.root}
        initialValues={initialValues}
        form={form}
        validateTrigger={isClikedSubmit ? 'onChange' : 'onSubmit'}
        onFinish={handleFinish}
        onFinishFailed={() => setClikedSubmit(true)}
      >
        <div className={styles.grid}>
          <div className={styles.fieldset}>
            <FormItem name="title" label="Drawdown title" rules={FIELD_RULES}>
              <Input ref={inputRef} size="large" />
            </FormItem>
            <FormItem
              name="requestedAmount"
              label="Requested amount, $ nzd"
              rules={FIELD_RULES}
            >
              <InputAmount />
            </FormItem>
          </div>
          <FormItem name="notes" label="Notes">
            <TextArea rows={7} />
          </FormItem>
        </div>
        <Text weight="demiBold" mb={2}>
          Documents
        </Text>
        {
          documentsError &&
          <Text mb={2} size='xs' color='redError'>
            Provide at least one of these documents
          </Text>
        }
        <FormItem
          noStyle
          className={styles.documents}
          name="documents"
          label="Provide at least one of these documents"
          rules={documentsRules}
        >
          <>
            <DocumentsUpload
              type="drawdownNotices"
              files={drawdownNotices}
              setFiles={setDrawdownNotices}
              isCreateDrawdown
              handleFileChange={handleUpload}
            />
            <DocumentsUpload
              type="invoices"
              files={invoices}
              setFiles={setInvoices}
              isCreateDrawdown
              handleFileChange={handleUpload}
            />
            <DocumentsUpload
              type="developmentSitePhotos"
              files={developmentSitePhotos}
              setFiles={setDevelopmentSitePhotos}
              isCreateDrawdown
              handleFileChange={handleUpload}
            />
            <div>
              <div className={styles.groupTitle}>
                <Space>
                  <FilesIcon />
                  <Text as="h4" weight="demiBold">
                    Certificates
                  </Text>
                </Space>
              </div>
              <FormItem noStyle name="certificates">
                <Checkbox.Group className={styles.group}>
                  {certificatesGroupEntries.map(([groupKey, groupData]) => (
                    <DocumentsUpload
                      key={groupKey}
                      isCreateDrawdown
                      checked={
                        certificatesValue
                          ? certificatesValue.includes(groupKey)
                          : false
                      }
                      type={groupKey as TDocumentsCollection}
                      files={groupData.documents}
                      setFiles={groupData.setDocuments}
                      handleFileChange={handleUpload}
                    />
                  ))}
                </Checkbox.Group>
              </FormItem>
            </div>
          </>
        </FormItem>
        <Box align="center" className={styles['option-area']}>
          <Button variant="secondary" loading={isMutating || isSaveMutating} className={styles.save} onClick={handleSave}>
            save
          </Button>
          <Button
            className={styles.button}
            htmlType="submit"
            loading={isMutating || isSaveMutating}
          >
            Submit
          </Button>
        </Box>
      </Form>
    </Spin>
  )
}

const getDocumentsUuids = (documents: FileInterface[]) =>
  documents.map((document) => document.uuid)

const formatDocumentsInfo = (documents: FileInterface[]) =>
  documents.map((document) => ({
    blob: {
      name: document.blob.name,
      size: document.blob.size,
      type: document.blob.type,
    },
    url: document.url,
    uuid: document.uuid,
  }))

const { TextArea } = Input
